import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FileText, Globe, MessageCircle, BarChart3, Loader2, RefreshCw } from 'lucide-react'
import api from '../api/client'

const STAT_CARDS = [
  { key: 'total_documents', label: 'Documents Uploaded', icon: FileText, color: 'bg-blue-500' },
  { key: 'total_translations', label: 'Translations Made', icon: Globe, color: 'bg-primary' },
  { key: 'total_chats', label: 'Questions Asked', icon: MessageCircle, color: 'bg-purple-500' },
  { key: 'languages_used', label: 'Languages Used', icon: BarChart3, color: 'bg-amber-500' },
]

function DashboardPage() {
  const [stats, setStats] = useState(null)
  const [languageBreakdown, setLanguageBreakdown] = useState([])
  const [recentActivity, setRecentActivity] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchData = async () => {
    setLoading(true)
    try {
      const [statsRes, langRes, activityRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/language-breakdown'),
        api.get('/dashboard/recent-activity'),
      ])
      setStats(statsRes.data)
      setLanguageBreakdown(langRes.data)
      setRecentActivity(activityRes.data)
    } catch (err) {
      console.error('Failed to load dashboard:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const maxLangCount = Math.max(...languageBreakdown.map(l => l.count), 1)

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1>Dashboard</h1>
          <p className="text-gray-500 mt-1">Platform usage and translation analytics</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {STAT_CARDS.map(({ key, label, icon: Icon, color }) => (
          <div key={key} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-navy">{stats?.[key] ?? 0}</p>
                <p className="text-sm text-gray-500">{label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Language Breakdown */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="mb-4">Language Breakdown</h2>
          {languageBreakdown.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No translations yet</p>
          ) : (
            <div className="space-y-3">
              {languageBreakdown.map((lang) => (
                <div key={lang.language_code} className="flex items-center gap-3">
                  <span className="w-24 text-sm font-medium text-gray-700 truncate">
                    {lang.native_name || lang.language_code}
                  </span>
                  <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                    <div
                      className="bg-primary h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                      style={{ width: `${Math.max((lang.count / maxLangCount) * 100, 8)}%` }}
                    >
                      <span className="text-xs font-medium text-white">{lang.count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="mb-4">Recent Documents</h2>
          {recentActivity.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No documents uploaded yet</p>
          ) : (
            <div className="space-y-2">
              {recentActivity.map((doc) => (
                <Link
                  key={doc.id}
                  to={`/translate/${doc.id}`}
                  className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-gray-400 group-hover:text-primary shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-navy truncate">{doc.filename}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(doc.upload_timestamp).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${
                    doc.status === 'ready' ? 'bg-green-100 text-green-700' :
                    doc.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {doc.status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
